import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Consultation from "@/components/LeadForm";
import Footer from "@/components/Footer";
import { FadeUp, scrollToId } from "@/components/motion";
import Seo from "@/lib/seo";
import { PATHWAYS } from "@/data/services";

export function enquire(interest) {
  sessionStorage.setItem("enquiry_interest", interest);
  window.dispatchEvent(new CustomEvent("enquiry-interest", { detail: interest }));
  scrollToId("#consultation");
}

export function PathwayCard({ p, delay = 0 }) {
  return (
    <FadeUp delay={delay} className="flex">
      <div data-testid={`pathway-${p.slug}`} className="flex flex-col w-full border border-[#27272A] bg-[#0A0A0A] p-8 md:p-10">
        <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00]">{p.eyebrow}</p>
        <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold tracking-tight text-[#F8F8F5]">{p.title}</h3>
        <p className="mt-4 font-body text-[18px] text-[#D4D4D8] leading-[1.6]">{p.summary}</p>
        <ul className="mt-6 space-y-3 flex-1">
          {p.included.map((it, i) => (
            <li key={i} className="flex gap-3 font-body text-[18px] text-[#D4D4D8] leading-[1.6]">
              <span className="mt-1.5 w-5 h-5 shrink-0 rounded-full bg-[#DFFF00] flex items-center justify-center">
                <Check className="text-[#0A0A0A]" size={12} />
              </span>
              {it}
            </li>
          ))}
        </ul>
        <p className="mt-6 font-body text-[16px] text-[#A1A1AA] leading-[1.6]">{p.note}</p>
        <p className="mt-6 font-body text-[18px] text-[#F8F8F5] leading-[1.6] border-l-2 border-[#DFFF00] pl-4">{p.pricing}</p>
        <button
          data-testid={p.ctaTestId}
          onClick={() => enquire(p.interest)}
          className="mt-8 w-full sm:w-auto rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors"
        >
          {p.cta} <ArrowRight size={17} />
        </button>
      </div>
    </FadeUp>
  );
}

export default function ServicesIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#0A0A0A] text-[#F8F8F5] min-h-screen">
      <Seo
        title="Services — Career Strategy, CV, LinkedIn and Small Business Support | Barrett Career Brand"
        description="Two clear ways we can help: Career Strategy, CV and LinkedIn as one combined service, or tailored Small Business and Self Employment programmes. Contact us for a clear quote before you decide."
        path="/services"
      />
      <header className="border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="services-page-logo" className="font-display text-lg font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
          <Link to="/" data-testid="services-page-home-link" className="font-body text-sm text-[#A1A1AA] hover:text-[#F8F8F5] transition-colors">
            ← Back to home
          </Link>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <FadeUp>
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Services</p>
          <h1 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-[#F8F8F5] max-w-4xl">
            Two clear ways <span className="font-serif-accent font-light">we can help.</span>
          </h1>
          <p className="mt-6 font-body text-[18px] md:text-[20px] text-[#D4D4D8] leading-[1.6] max-w-3xl">
            Think of your career as your professional offering. What can you help people achieve?
            We help you bring your skills, experience and results together into a clear pitch,
            then build your CV and LinkedIn profile around it. You do not need a business idea
            to use the career service.
          </p>
          <p className="mt-4 font-body text-[16px] text-[#A1A1AA] leading-[1.6] max-w-3xl">
            Not ready to talk yet? Start with the{" "}
            <a href="/#cv-review" data-testid="services-free-score-link" className="text-[#DFFF00] font-semibold hover:underline underline-offset-4">
              free CV assessment
            </a>{" "}
            — it is complimentary and separate from our paid support.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16 grid lg:grid-cols-2 gap-6">
        {PATHWAYS.map((p, i) => (
          <PathwayCard key={p.slug} p={p} delay={i * 0.08} />
        ))}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12">
        <FadeUp>
          <div data-testid="how-purchasing-works" className="border border-[#27272A] bg-[#141414] p-8 md:p-10">
            <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00]">How purchasing works</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-8">
              {[
                ["1. Request a quote", "Tell us what you need using the enquiry form. There is no charge and no commitment."],
                ["2. Receive your written quote", "We email you a quote showing the service, inclusions, total price in AUD, tax treatment, delivery timeframe and included revisions."],
                ["3. Accept and pay securely", "If you are happy, accept the quote and pay on a secure Stripe payment page. You then receive a receipt and simple next steps."],
              ].map(([t, d]) => (
                <div key={t}>
                  <p className="font-display font-bold text-[#F8F8F5]">{t}</p>
                  <p className="mt-2 font-body text-[15px] text-[#A1A1AA] leading-[1.6]">{d}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-body text-sm text-[#52525B] leading-relaxed">
              Before you pay, you can read our{" "}
              <Link to="/terms" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">service terms</Link>,{" "}
              <Link to="/privacy" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">privacy policy</Link> and{" "}
              <Link to="/refunds" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">refunds and complaints information</Link>.
              We never ask for card details by email, phone or chat.
            </p>
          </div>
        </FadeUp>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
        <FadeUp>
          <p className="font-body text-[18px] text-[#A1A1AA] leading-[1.6] max-w-3xl">
            Not sure which pathway fits? Choose{" "}
            <button
              data-testid="enquire-unsure-btn"
              onClick={() => enquire("I am unsure and would like guidance")}
              className="text-[#DFFF00] font-semibold hover:underline underline-offset-4"
            >
              "I am unsure and would like guidance"
            </button>{" "}
            in the enquiry form below. Sending an enquiry does not commit you to purchasing a service.
          </p>
        </FadeUp>
      </section>

      <Consultation />
      <Footer />
    </main>
  );
}
